/**
 * Word selector for Word Drift
 * Selects up to 8 strategic clickable words for display
 */

import { logger } from '@mental-mint/shared-utils';
import type { DictionaryEntry } from './DictionaryTypes';
import type { EmbeddingsManager } from './EmbeddingsManager';
import type { GameWord } from './GameTypes';
import { findPath } from './Pathfinding';
import { WORD_SELECTION_CONSTANTS } from './GameConstants';
import { getAllNeighbors } from './NeighborExtractor';

const MAX_NON_SYNSET_WORDS = 3;

/**
 * Helper function to check if we should limit to synset-only words
 * Returns filtered neighbors (only synset words) if we already have 3+ non-synset words
 */
function limitNonSynsetWords(
  neighbors: GameWord[],
  synsetWords: Set<string>,
  selected: Set<string>
): GameWord[] {
  // Count how many non-synset words we already have
  const nonSynsetCount = Array.from(selected).filter(w => !synsetWords.has(w)).length;
  
  if (nonSynsetCount >= MAX_NON_SYNSET_WORDS) {
    // We already have 3+ non-synset words, limit remaining to synset words only
    const filtered = neighbors.filter(gw => synsetWords.has(gw.variations.lemmatized));
    // logger.debug(`[WordSelector] Limited to synset words only (already have ${nonSynsetCount} non-synset words)`);
    return filtered;
  }
  
  return neighbors;
}

/**
 * Get next word from optimal path if current word is on the path
 */
function getNextOptimalWord(currentWord: string, optimalPath?: GameWord[]): string | null {
  if (!optimalPath || optimalPath.length === 0) return null;
  
  const currentLower = currentWord.toLowerCase();
  const currentIndex = optimalPath.findIndex(gw => gw.variations.lemmatized.toLowerCase() === currentLower);
  
  if (currentIndex >= 0 && currentIndex < optimalPath.length - 1) {
    const nextWord = optimalPath[currentIndex + 1].variations.onClickDestination;
    // logger.debug(`[WordSelector] Next optimal path word: "${nextWord}"`);
    return nextWord;
  }
  
  return null;
}

/**
 * Rank neighbors by cosine similarity to goal word
 */
function rankNeighborsByGoalSimilarity(
  neighbors: GameWord[],
  goalWord: string,
  embeddingsManager: EmbeddingsManager
): Array<{ gameWord: GameWord; similarity: number }> {
  const ranked = neighbors
    .map(gameWord => {
      const similarity = embeddingsManager.similarity(gameWord.variations.onClickDestination, goalWord) || 0;
      return { gameWord, similarity };
    })
    .sort((a, b) => b.similarity - a.similarity);
  
  return ranked;
}

/**
 * Rank neighbors by cosine similarity to current word
 */
function rankNeighborsByCurrentSimilarity(
  neighbors: GameWord[],
  currentWord: string,
  embeddingsManager: EmbeddingsManager
): Array<{ gameWord: GameWord; similarity: number }> {
  const ranked = neighbors
    .map(gameWord => {
      const similarity = embeddingsManager.similarity(currentWord, gameWord.variations.onClickDestination) || 0;
      return { gameWord, similarity };
    })
    .sort((a, b) => b.similarity - a.similarity);
  
  return ranked;
}

/**
 * Ensure at least one synset word is selected to guarantee synset visibility
 * If no synset words are present, replaces the least useful non-synset word with the best synset word
 */
function ensureAtLeastOneSynsetWord(
  selected: Set<string>,
  neighbors: GameWord[],
  synsetWords: Set<string>,
  goalWord: string,
  embeddingsManager: EmbeddingsManager
): void {
  const hasSynsetWord = Array.from(selected).some(word => synsetWords.has(word));
  
  if (!hasSynsetWord && selected.size > 0) {
    // Find the best synset word from all neighbors (unfiltered)
    const synsetNeighbors = neighbors.filter(gw => 
      synsetWords.has(gw.variations.lemmatized) && 
      !selected.has(gw.variations.onClickDestination)
    );
    
    if (synsetNeighbors.length > 0) {
      // Rank synset words by goal similarity and pick the best one
      const rankedSynsetWords = rankNeighborsByGoalSimilarity(synsetNeighbors, goalWord, embeddingsManager);
      const bestSynsetWord = rankedSynsetWords[0];
      
      // Replace a non-synset word with the best synset word
      const nonSynsetWords = Array.from(selected).filter(word => !synsetWords.has(word));
      if (nonSynsetWords.length > 0) {
        // Remove the least useful non-synset word (last one added, typically)
        selected.delete(nonSynsetWords[nonSynsetWords.length - 1]);
        selected.add(bestSynsetWord.gameWord.variations.onClickDestination);
        logger.debug(`[WordSelector] Ensured synset word: replaced "${nonSynsetWords[nonSynsetWords.length - 1]}" with "${bestSynsetWord.gameWord.variations.onClickDestination}"`);
      }
    }
  }
}

/**
 * Select clickable words according to strategic priority:
 * 1. Next word from optimal path (if current word is on path)
 * 2. Next word from A* path
 * 3. Neighbors that are also neighbors of the goal word (common neighbors)
 * 4. 2 neighbors with highest cosine similarity to goal
 * 5. 1 neighbor with lowest cosine similarity to goal (red herring)
 * 6. Fill remaining slots with neighbors having highest similarity to current word
 * 
 * Progressive revelation (to avoid overwhelming new players):
 * - Path length 1 (starting word): show only 2 words
 * - Path length 2: show 3 words
 * - Path length 3+: show up to MAX_CLICKABLE_WORDS (6)
 * 
 * NOTE: Limits non-synset words to 3 by filtering remaining neighbors after we reach that threshold
 * NOTE: Goal word inclusion is handled in getAllNeighbors (step 5 & 6)
 * 
 * @param currentWord - Current word
 * @param goalWord - Goal word
 * @param optimalPath - Optimal path from puzzle generation
 * @param neighbors - All valid neighbors as GameWords
 * @param embeddingsManager - Embeddings manager for similarity calculations
 * @param dictionary - Dictionary for pathfinding
 * @param synsetWords - Set of lemmatized words that appear in synset definitions (for limiting non-synset words)
 * @param pathLength - Current path length (defaults to 3+ if not provided)
 * @returns Array of selected words (deduplicated)
 */
export function selectClickableWords(
  currentWord: string,
  goalWord: string,
  optimalPath: GameWord[] | undefined,
  neighbors: GameWord[],
  embeddingsManager: EmbeddingsManager,
  dictionary: Record<string, DictionaryEntry>,
  synsetWords?: Set<string>,
  pathLength?: number
): string[] {
  const selected = new Set<string>();
  const currentLower = currentWord.toLowerCase();
  const goalLower = goalWord.toLowerCase();
  const synset = synsetWords || new Set<string>();
  
  // Progressive word revelation to avoid overwhelming new players
  const maxWords = pathLength === 1 ? 2 : pathLength === 2 ? 3 : WORD_SELECTION_CONSTANTS.MAX_CLICKABLE_WORDS;
  
  // Remove current and goal words from neighbors (will be added separately if appropriate)
  let availableNeighbors = neighbors.filter(gw => gw.variations.onClickDestination !== currentLower && gw.variations.onClickDestination !== goalLower);
  
  if (availableNeighbors.length === 0) {
    // logger.debug(`[WordSelector] No neighbors available for "${currentWord}"`);
    return [];
  }
  
  // 1. Add next word from optimal path
  const optimalWord = getNextOptimalWord(currentWord, optimalPath);
  if (optimalWord && availableNeighbors.some(gw => gw.variations.onClickDestination === optimalWord)) {
    selected.add(optimalWord);
    // logger.debug(`[WordSelector] Added optimal path word: "${optimalWord}"`);
  }
  
  // Apply non-synset word limit after adding optimal word
  availableNeighbors = limitNonSynsetWords(availableNeighbors, synset, selected);
  
  // 2. Add next word from A* path
  try {
    const pathResult = findPath(currentWord, goalWord, dictionary, embeddingsManager);
    if (pathResult.found && pathResult.path.length > 1) {
      const aStarNextWord = pathResult.path[1]; // First word after current
      if (aStarNextWord !== goalLower && availableNeighbors.some(gw => gw.variations.onClickDestination === aStarNextWord) && !selected.has(aStarNextWord)) {
        selected.add(aStarNextWord);
        // logger.debug(`[WordSelector] Added A* next word: "${aStarNextWord}" (path length: ${pathResult.path.length})`);
      }
    }
  } catch (error) {
    // logger.debug(`[WordSelector] Could not find A* path:`, error);
  }
  
  // Apply non-synset word limit after adding A* word
  availableNeighbors = limitNonSynsetWords(availableNeighbors, synset, selected);
  
  // 3. Add neighbors that are also neighbors of the goal word (common neighbors)
  const goalNeighbors = getAllNeighbors(goalWord, dictionary, undefined, embeddingsManager);
  const goalNeighborDestinations = new Set(goalNeighbors.map(gw => gw.variations.onClickDestination));
  
  const commonNeighbors = availableNeighbors.filter(gw => 
    goalNeighborDestinations.has(gw.variations.onClickDestination) && 
    !selected.has(gw.variations.onClickDestination)
  );
  
  for (const neighbor of commonNeighbors) {
    if (selected.size >= maxWords) break;
    selected.add(neighbor.variations.onClickDestination);
    // logger.debug(`[WordSelector] Added common neighbor: "${neighbor.variations.onClickDestination}"`);
    
    // Re-filter after each addition
    availableNeighbors = limitNonSynsetWords(availableNeighbors, synset, selected);
  }
  
  // 4. Add up to 2 neighbors with highest similarity to goal (add one at a time, re-filter after each)
  let goalSimilarAdded = 0;
  while (goalSimilarAdded < WORD_SELECTION_CONSTANTS.NUM_GOAL_SIMILAR_WORDS && selected.size < maxWords) {
    const byGoalSimilarity = rankNeighborsByGoalSimilarity(availableNeighbors, goalWord, embeddingsManager);
    const candidate = byGoalSimilarity.find(({ gameWord }) => !selected.has(gameWord.variations.onClickDestination));
    
    if (!candidate) break;
    
    selected.add(candidate.gameWord.variations.onClickDestination);
    goalSimilarAdded++;
    logger.debug(`[WordSelector] Added high goal similarity word: "${candidate.gameWord.variations.onClickDestination}" (${candidate.similarity.toFixed(3)})`);
    
    // Re-filter after each addition
    availableNeighbors = limitNonSynsetWords(availableNeighbors, synset, selected);
  }
  
  // 5. Add 1 neighbor with lowest similarity to goal (red herring)
  if (selected.size < maxWords) {
    const byGoalSimilarity = rankNeighborsByGoalSimilarity(availableNeighbors, goalWord, embeddingsManager);
    const candidate = byGoalSimilarity.reverse().find(({ gameWord }) => !selected.has(gameWord.variations.onClickDestination));
    
    if (candidate) {
      selected.add(candidate.gameWord.variations.onClickDestination);
      // logger.debug(`[WordSelector] Added red herring word: "${candidate.gameWord.variations.onClickDestination}" (${candidate.similarity.toFixed(3)})`);
      
      // Re-filter after adding red herring
      availableNeighbors = limitNonSynsetWords(availableNeighbors, synset, selected);
    }
  }
  
  // 6. Fill remaining slots with neighbors having highest similarity to current word (add one at a time)
  while (selected.size < maxWords) {
    const byCurrentSimilarity = rankNeighborsByCurrentSimilarity(availableNeighbors, currentWord, embeddingsManager);
    const candidate = byCurrentSimilarity.find(({ gameWord }) => !selected.has(gameWord.variations.onClickDestination));
    
    if (!candidate) break;
    
    selected.add(candidate.gameWord.variations.onClickDestination);
    // logger.debug(`[WordSelector] Added current similarity filler: "${candidate.gameWord.variations.onClickDestination}" (${candidate.similarity.toFixed(3)})`);
    
    // Re-filter after each addition
    availableNeighbors = limitNonSynsetWords(availableNeighbors, synset, selected);
  }
  
  // 7. Ensure at least one synset word is selected to guarantee synset visibility
  ensureAtLeastOneSynsetWord(selected, neighbors, synset, goalWord, embeddingsManager);
  
  const result = Array.from(selected);
  // logger.debug(`[WordSelector] Selected ${result.length} clickable words for "${currentWord}"`);
  return result;
}
